import { Group } from '../types';
import {
  TogglProject,
  TogglProjectExtended,
  TogglTag,
  TogglTask,
  TogglTimeEntry,
  TogglWorkspace,
} from '../types/toggl-api';

export function groupProjectsByWorkspace(
  projects: TogglProjectExtended[]
): Group<TogglProjectExtended[]> {
  return projects.reduce<Group<TogglProjectExtended[]>>((result, project) => {
    const { workspace_id } = project;

    if (!result[workspace_id]) {
      result[workspace_id] = [];
    }

    result[workspace_id].push(project);
    return result;
  }, {});
}

export function groupWorkspaces(
  workspaces: TogglWorkspace[]
): Group<TogglWorkspace> {
  return workspaces.reduce<Group<TogglWorkspace>>((result, workspace) => {
    result[workspace.id] = workspace;
    return result;
  }, {});
}

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  return array.reduce((resultArray: T[][], item, index) => {
    const chunkIndex = Math.floor(index / chunkSize);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
}

export function groupProjects(projects: TogglProject[]): Group<TogglProject> {
  return projects.reduce<Group<TogglProject>>((result, project) => {
    result[project.id] = project;
    return result;
  }, {});
}

export function extractIssueKeyFromTimeEntry(
  togglTimeEntry: TogglTimeEntry,
  {
    projects,
    tasks,
    tags,
  }: { projects: TogglProject[]; tasks?: TogglTask[]; tags?: TogglTag[] }
) {
  const { description, project_id, task_id, tag_ids } = togglTimeEntry;

  const issueKeyFromDescription = extractKey(description);
  if (issueKeyFromDescription) {
    return issueKeyFromDescription;
  }

  const projectName =
    projects.find((project) => project.id === project_id)?.name || '';
  const issueKeyFromProjectName = extractKey(projectName);
  if (issueKeyFromProjectName) {
    return issueKeyFromProjectName;
  }

  const taskName = tasks?.find((task) => task.id === task_id)?.name || '';
  const issueKeyFromTaskName = extractKey(taskName);
  if (issueKeyFromTaskName) {
    return issueKeyFromTaskName;
  }

  return (
    tag_ids
      ?.map((tagId) => tags?.find((tag) => tag.id === tagId))
      .map((tag) => tag?.name)
      .filter(Boolean) as string[] | undefined
  )?.reduce((acc: string | undefined, tag: string) => {
    if (!acc) {
      return extractKey(tag);
    }
    return acc;
  }, undefined);
}

function extractKey(stringWithIssueKey: string) {
  if (!stringWithIssueKey) {
    return undefined;
  }
  const issueKeyPattern = /[a-zA-Z0-9]+-[0-9]+/i;
  const match = stringWithIssueKey.match(issueKeyPattern);
  return match !== null ? match[0].trim() : undefined;
}

export const isEllipsisActive = (el: HTMLDivElement | null) => {
  if (!el) return false;
  return el?.offsetWidth < el?.scrollWidth;
};

export function getIframeUrlParameterByName(name: string) {
  const params = new URL(document.location.toString()).searchParams;
  return params.get(name);
}
